const R = 6_378_137 // WGS-84 equatorial radius, metres
const rad = (deg: number) => (deg * Math.PI) / 180

/**
 * Spherical-excess area of a ring, in m². Within ~0.3% of the spheroid figure
 * PostGIS reports, which is close enough for the live tooltip in 2a; the
 * authoritative area always comes back from the server.
 */
function ringArea(ring: [number, number][]): number {
  if (ring.length < 3) return 0
  let total = 0
  for (let i = 0; i < ring.length; i++) {
    const [lon1, lat1] = ring[i]!
    const [lon2, lat2] = ring[(i + 1) % ring.length]!
    total += (rad(lon2) - rad(lon1)) * (2 + Math.sin(rad(lat1)) + Math.sin(rad(lat2)))
  }
  return Math.abs((total * R * R) / 2)
}

export function polygonAreaHa(geometry: GeoJSON.Geometry | null | undefined): number {
  if (!geometry) return 0
  if (geometry.type === 'Polygon') {
    const [outer, ...holes] = geometry.coordinates as [number, number][][]
    const area = ringArea(outer ?? []) - holes.reduce((s, h) => s + ringArea(h), 0)
    return Math.max(0, area) / 10_000
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates as [number, number][][][]).reduce(
      (sum, poly) => sum + polygonAreaHa({ type: 'Polygon', coordinates: poly } as GeoJSON.Polygon),
      0,
    )
  }
  return 0
}

/** Haversine distance in metres. */
export function distanceM([lon1, lat1]: [number, number], [lon2, lat2]: [number, number]): number {
  const dLat = rad(lat2 - lat1)
  const dLon = rad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

export function perimeterM(geometry: GeoJSON.Geometry | null | undefined): number {
  if (!geometry || geometry.type !== 'Polygon') return 0
  const ring = (geometry.coordinates as [number, number][][])[0] ?? []
  let total = 0
  for (let i = 0; i < ring.length - 1; i++) total += distanceM(ring[i]!, ring[i + 1]!)
  return total
}

export function vertexCount(geometry: GeoJSON.Geometry | null | undefined): number {
  if (!geometry || geometry.type !== 'Polygon') return 0
  const ring = (geometry.coordinates as [number, number][][])[0] ?? []
  // The closing coordinate repeats the first, so it is not a distinct point.
  return Math.max(0, ring.length - 1)
}

/** Centre of a geometry's bounding box — good enough to place a label. */
export function centroid(geometry: GeoJSON.Geometry | null | undefined): [number, number] | null {
  if (!geometry) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  const walk = (c: unknown): void => {
    if (!Array.isArray(c)) return
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      minX = Math.min(minX, c[0] as number)
      maxX = Math.max(maxX, c[0] as number)
      minY = Math.min(minY, c[1] as number)
      maxY = Math.max(maxY, c[1] as number)
      return
    }
    for (const x of c) walk(x)
  }
  walk((geometry as { coordinates?: unknown }).coordinates)
  if (!Number.isFinite(minX)) return null
  return [(minX + maxX) / 2, (minY + maxY) / 2]
}

/** Accepts `5.7988, -6.6014` or `5.7988 N, 6.6014 W`. */
export function parseCoordinates(input: string): [number, number] | null {
  const cleaned = input.trim().replace(/[°]/g, '')
  const match = cleaned.match(
    /^(-?\d+(?:\.\d+)?)\s*([NnSs])?\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*([EeWw])?$/,
  )
  if (!match) return null
  let lat = Number(match[1])
  let lon = Number(match[3])
  if (match[2]?.toUpperCase() === 'S') lat = -Math.abs(lat)
  if (match[4]?.toUpperCase() === 'W') lon = -Math.abs(lon)
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
  return [lon, lat]
}
